// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
import { chatApi } from "../../api/modules/chat";
export type CopyableContent = {
  type?: string;
  text?: string;
  refusal?: string;
};

export type CopyableMessage = {
  role?: string;
  type?: string;
  content?: string | CopyableContent[];
};

export type CopyableResponse = {
  output?: CopyableMessage[];
};

export type RuntimeLoadingBridgeApi = {
  getLoading?: () => boolean | string;
  setLoading?: (loading: boolean | string) => void;
};

// ---------------------------------------------------------------------------
// Text extraction utilities
// ---------------------------------------------------------------------------

/** Extract copyable text from assistant response. */
export function extractCopyableText(response: CopyableResponse): string {
  const chunks = (response.output || []).flatMap((item: CopyableMessage) => {
    if (item.role !== "assistant") return [];

    // Runtime reasoning, tool calls, and tool results also use the assistant
    // role. Only ordinary assistant messages belong on the clipboard.
    if (item.type !== "message") return [];

    if (typeof item.content === "string") {
      return [item.content];
    }

    if (!Array.isArray(item.content)) {
      return [];
    }

    return item.content.flatMap((content: CopyableContent) => {
      if (content.type === "text" && typeof content.text === "string") {
        return [content.text];
      }

      if (content.type === "refusal" && typeof content.refusal === "string") {
        return [content.refusal];
      }

      return [];
    });
  });

  return chunks.filter(Boolean).join("\n\n").trim();
}

/** Extract plain text from user message content. */
export function extractUserMessageText(m: any): string {
  if (typeof m.content === "string") return m.content;
  if (!Array.isArray(m.content)) return "";
  return m.content
    .filter((p: any) => p.type === "text")
    .map((p: any) => p.text || "")
    .join("\n");
}

export function extractTextFromMessage(msg: any): string {
  const innerMessage = msg?.cards?.[0]?.data?.input?.[0];
  if (!innerMessage) return "";
  return extractUserMessageText(innerMessage);
}

// ---------------------------------------------------------------------------
// Clipboard utilities
// ---------------------------------------------------------------------------

export { copyText } from "../../utils/clipboard";

// ---------------------------------------------------------------------------
// Timestamp formatting utilities
// ---------------------------------------------------------------------------

/** Format a unix timestamp (seconds or milliseconds) to a short time string (HH:mm:ss). */
export function formatMessageTime(ts: number): string {
  if (!ts) return "";
  // Normalize to milliseconds
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const date = new Date(ms);
  const now = new Date();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  const time = `${hours}:${minutes}:${seconds}`;

  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (isToday) return time;

  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  if (date.getFullYear() === now.getFullYear()) {
    return `${month}-${day} ${time}`;
  }
  return `${date.getFullYear()}-${month}-${day} ${time}`;
}

// ---------------------------------------------------------------------------
// Error response utilities
// ---------------------------------------------------------------------------

/** Build a 400 error response when model is not configured. */
export function buildModelError(): Response {
  return new Response(
    JSON.stringify({
      error: "Model not configured",
      message: "Please configure a model first",
    }),
    { status: 400, headers: { "Content-Type": "application/json" } },
  );
}

// ---------------------------------------------------------------------------
// URL normalization utilities
// ---------------------------------------------------------------------------

/** Decode each path segment; keeps `/` delimiters (including repeated `/`). */
function decodeUriPathSegments(path: string): string {
  return path
    .split("/")
    .map((segment) => {
      if (!segment) return segment;
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    })
    .join("/");
}

/** Convert file URL to stored path for backend: keep full path after `/files/preview/`. */
export function toStoredName(v: string): string {
  const marker = "/files/preview/";
  const idx = v.indexOf(marker);
  if (idx !== -1) {
    let rest = v.slice(idx + marker.length);
    const q = rest.indexOf("?");
    if (q !== -1) rest = rest.slice(0, q);
    const h = rest.indexOf("#");
    if (h !== -1) rest = rest.slice(0, h);
    if (rest) {
      const decoded = decodeUriPathSegments(rest);
      // Windows absolute path: C:\... or C:/...
      const isWindowsAbsolute = /^[a-zA-Z]:[\\/]/.test(decoded);
      if (isWindowsAbsolute) return decoded;
      return decoded.startsWith("/") ? decoded : `/${decoded}`;
    }
  }
  return v;
}

/** Convert content part URLs to stored name format. */
export function normalizeContentUrls(part: any): any {
  const p = { ...part };
  if (p.type === "image" && typeof p.image_url === "string")
    p.image_url = toStoredName(p.image_url);
  if (p.type === "file" && typeof p.file_url === "string")
    p.file_url = toStoredName(p.file_url);
  if (p.type === "audio" && typeof p.data === "string")
    p.data = toStoredName(p.data);
  if (p.type === "video" && typeof p.video_url === "string")
    p.video_url = toStoredName(p.video_url);
  return p;
}

/** Turn a backend content URL (path or full URL) into a full URL for display. */
export function toDisplayUrl(url: string | undefined): string {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  // Data URLs (base64 images etc.) must pass through untouched — routing
  // them through filePreviewUrl would corrupt the URL and break rendering
  // of historical messages on session reload.
  if (url.startsWith("data:")) return url;
  if (url.startsWith("file://")) url = url.replace("file://", "");
  return chatApi.filePreviewUrl(url.startsWith("/") ? url : `/${url}`);
}

// ---------------------------------------------------------------------------
// DOM utilities
// ---------------------------------------------------------------------------

/** Return the sender textarea belonging to the focused sender surface. */
export function getActiveSenderTextarea(): HTMLTextAreaElement | null {
  const focused =
    document.activeElement instanceof HTMLElement
      ? document.activeElement.closest('[class*="sender"]')
      : null;
  const focusedTextarea = focused?.querySelector("textarea");
  if (focusedTextarea instanceof HTMLTextAreaElement) {
    return focusedTextarea;
  }

  const candidates = Array.from(
    document.querySelectorAll<HTMLTextAreaElement>(
      '[class*="sender"] textarea',
    ),
  );
  return (
    candidates.find((textarea) => textarea.offsetParent !== null) ??
    candidates[candidates.length - 1] ??
    null
  );
}

/** Resolve the sender's state textarea from its textarea or rich editor. */
export function getSenderTextareaFromTarget(
  target: EventTarget | null,
): HTMLTextAreaElement | null {
  if (!(target instanceof HTMLElement)) return null;

  const sender = target.closest('[class*="sender"]');
  if (!sender) return null;

  if (target instanceof HTMLTextAreaElement) {
    return target;
  }

  const isRichEditor =
    target.isContentEditable ||
    target.getAttribute("contenteditable") === "true";
  if (!isRichEditor) return null;
  const textarea = sender.querySelector("textarea");
  return textarea instanceof HTMLTextAreaElement ? textarea : null;
}

/** Set textarea value and trigger input event for React state sync.
 * Uses native value setter to bypass React's internal value tracker.
 */
export function setTextareaValue(textarea: HTMLTextAreaElement, value: string) {
  const nativeValueSetter = Object.getOwnPropertyDescriptor(
    HTMLTextAreaElement.prototype,
    "value",
  )?.set;
  if (nativeValueSetter) {
    nativeValueSetter.call(textarea, value);
  } else {
    textarea.value = value;
  }
  textarea.selectionStart = textarea.selectionEnd = value.length;
  const event = new Event("input", { bubbles: true });
  textarea.dispatchEvent(event);
}

/**
 * Clear the submitted sender value without erasing text typed for the next
 * message while the request was being prepared.
 */
export function clearSubmittedSenderInput(submittedValue: string): boolean {
  const textarea = getActiveSenderTextarea();
  if (!textarea || textarea.value !== submittedValue) {
    return false;
  }
  setTextareaValue(textarea, "");
  return true;
}
